/**
 * CommsMomentum — a read-only model of how the current run is *going*, used by
 * the HUD commentary director so beats can react to trajectory (streaks,
 * clawing back from the brink, who is winning) instead of isolated events.
 *
 * Boundaries (must stay true):
 *  - Pure logic. No DOM, no EventBus, no simulation / RuntimeState access.
 *  - No randomness, no wall-clock. Fully deterministic given its inputs, which
 *    keeps it trivially unit-testable and keeps it off the simulation's
 *    determinism surface entirely (it lives in the UI layer).
 *
 * The director feeds it discrete signals (wave boundaries, core hits) and asks
 * it questions (clean-wave streak, comeback, rivalry tone).
 */

export type RivalryTone =
  /** Player is dominating — long clean streak. FENRIR loses his grip. */
  | "rattled"
  /** Player is losing ground — FENRIR presses the advantage. */
  | "ascendant"
  /** Business as usual. */
  | "even";

export interface MomentumConfig {
  /** Lives at/below this count means the player is in real danger. */
  lowLives: number;
  /** Consecutive clean waves before the player counts as "dominating". */
  dominanceStreak?: number;
}

export interface WaveOutcome {
  /** Consecutive waves cleared without taking core damage. */
  cleanStreak: number;
  /** True when the clean streak just hit a dominance milestone. */
  flawlessMilestone: boolean;
  /** True when the player just clawed back to safety after a near-death dip. */
  stabilizedFromBrink: boolean;
}

const DEFAULT_DOMINANCE_STREAK = 3;
/** Clean waves after a brink dip before we call it a stabilized comeback. */
const STABILIZE_WAVES = 2;

export class CommsMomentum {
  private readonly lowLives: number;
  private readonly dominanceStreak: number;
  private readonly dangerThreshold: number;

  private cleanWaveStreak = 0;
  private hitsThisWave = 0;
  private wasInDanger = false;
  private cleanWavesSinceDanger = 0;

  constructor(config: MomentumConfig) {
    this.lowLives = config.lowLives;
    this.dominanceStreak = Math.max(
      1,
      config.dominanceStreak ?? DEFAULT_DOMINANCE_STREAK,
    );
    // "Losing ground" starts before outright critical so FENRIR escalates
    // while the player still has a fighting chance.
    this.dangerThreshold = this.lowLives * 2;
  }

  /** Wipe all trajectory state for a fresh run. */
  reset(): void {
    this.cleanWaveStreak = 0;
    this.hitsThisWave = 0;
    this.wasInDanger = false;
    this.cleanWavesSinceDanger = 0;
  }

  /** A new wave has begun — start counting hits for it afresh. */
  onWaveStarted(): void {
    this.hitsThisWave = 0;
  }

  /** The core took a hit; `livesLeft` is the post-hit life count. */
  onCoreHit(livesLeft: number): void {
    this.hitsThisWave += 1;
    this.cleanWaveStreak = 0;
    if (livesLeft <= this.lowLives) {
      this.wasInDanger = true;
      this.cleanWavesSinceDanger = 0;
    }
  }

  /** A wave finished. Returns the trajectory facts the director reacts to. */
  onWaveCompleted(): WaveOutcome {
    const clean = this.hitsThisWave === 0;
    if (clean) this.cleanWaveStreak += 1;

    let stabilizedFromBrink = false;
    if (this.wasInDanger && clean) {
      this.cleanWavesSinceDanger += 1;
      if (this.cleanWavesSinceDanger >= STABILIZE_WAVES) {
        stabilizedFromBrink = true;
        // Consume the dip so the comeback line fires once per brush with death.
        this.wasInDanger = false;
        this.cleanWavesSinceDanger = 0;
      }
    }

    const flawlessMilestone =
      clean &&
      this.cleanWaveStreak > 0 &&
      this.cleanWaveStreak % this.dominanceStreak === 0;

    return {
      cleanStreak: this.cleanWaveStreak,
      flawlessMilestone,
      stabilizedFromBrink,
    };
  }

  /** Rivalry tone at the moment of a beat, given the current life count. */
  getTone(livesLeft: number): RivalryTone {
    if (this.cleanWaveStreak >= this.dominanceStreak) return "rattled";
    if (livesLeft <= this.dangerThreshold) return "ascendant";
    return "even";
  }
}
